import {
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence,
  type UserCredential,
  sendPasswordResetEmail,
  sendEmailVerification,
  deleteUser,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updateEmail,
  type User,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithPopup,
} from "firebase/auth";
import { firebaseAuth } from "@/lib/firebase-config";
import axiosClient from "@/lib/axios/axiosClient";
import type {
  UpdateUserAPIRequest,
  UpdateUserAPIResponse,
} from "@/common/types/user";
import type { PlanName, PlanStatusActive } from "@/common/enums/enums";
import { FirebaseError } from "firebase/app";
import { AUTH_ERRORS } from "@/common/errors";
import type { VisitRetentionPeriod } from "@/common/types/settingsOptions";
import type { TranscriptionLanguage } from "@/common/types/transcriptionLanguage";

void setPersistence(firebaseAuth, browserLocalPersistence);

export interface LoginFormValues {
  email: string;
  password: string;
}

export const signInUserWithEmail = async ({
  email,
  password,
}: LoginFormValues): Promise<UserCredential> => {
  const result = await signInWithEmailAndPassword(
    firebaseAuth,
    email,
    password
  );
  return result;
};

export const GOOGLE_AUTH_PROVIDER_ID = "google.com";

const googleAuthProvider = new GoogleAuthProvider();
googleAuthProvider.setCustomParameters({ prompt: "select_account" });

export const userHasGoogleProvider = (user: User): boolean =>
  user.providerData.some(
    (provider) => provider.providerId === GOOGLE_AUTH_PROVIDER_ID
  );

export const userHasPasswordProvider = (user: User): boolean =>
  user.providerData.some(
    (provider) => provider.providerId === EmailAuthProvider.PROVIDER_ID
  );

export const signInUserWithGoogle = async (): Promise<UserCredential> => {
  return signInWithPopup(firebaseAuth, googleAuthProvider);
};

export const linkGoogleAccountToCurrentUser = async (): Promise<User> => {
  const user = firebaseAuth.currentUser;
  if (!user) {
    throw new Error(AUTH_ERRORS.INVALID_CREDENTIALS);
  }
  if (userHasGoogleProvider(user)) {
    return user;
  }
  const result = await linkWithPopup(user, googleAuthProvider);
  return result.user;
};

export function parseOAuthDisplayName(displayName: string | null | undefined): {
  firstname: string;
  lastname: string;
} {
  const trimmed = displayName?.trim();
  if (!trimmed) {
    return { firstname: "", lastname: "" };
  }
  const parts = trimmed.split(/\s+/);
  return {
    firstname: parts[0] ?? "",
    lastname: parts.slice(1).join(" "),
  };
}

export interface AddUserOAuthParameters {
  firstname?: string;
  lastname?: string;
  occupationId?: number;
}

export const addUserOAuth = async (
  body: AddUserOAuthParameters
): Promise<{
  message: string;
  userId: number;
  created: boolean;
  fUidSynced?: boolean;
}> => {
  const response = await axiosClient.post<{
    message: string;
    userId: number;
    created: boolean;
    fUidSynced?: boolean;
  }>("/v2/add-user-oauth", body, {
    sendToken: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

export interface UserFormValues {
  email: string;
  password: string;
  displayName?: string;
}

export const signupUserWithEmail = async ({
  email,
  password,
}: UserFormValues): Promise<UserCredential> => {
  const result = await createUserWithEmailAndPassword(
    firebaseAuth,
    email,
    password
  );

  if (result?.user && !result.user.emailVerified) {
    await sendEmailVerification(result?.user);
  }

  return result;
};

export const signOutUser = async (): Promise<void> => {
  await signOut(firebaseAuth);
};

export const getCurrentUser = (): Promise<User | null> => {
  return new Promise((resolve) => {
    const unsubscribe = firebaseAuth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

export const deleteUserAuth = async (): Promise<void> => {
  const currentUser = await getCurrentUser();
  if (currentUser) {
    await deleteUser(currentUser);
  }
};

export const checkEmailVerified = async (): Promise<boolean> => {
  const user = await getCurrentUser();
  return user?.emailVerified || false;
};

export const sendPasswordResetWithEmail = async ({
  email,
}: Pick<LoginFormValues, "email">): Promise<void> => {
  await sendPasswordResetEmail(firebaseAuth, email);
};

export const changePasswordAuth = async (
  newPassword: string
): Promise<void> => {
  const currentUser = await getCurrentUser();
  if (currentUser) {
    await updatePassword(currentUser, newPassword);
  }
};

export const changeEmailAuth = async (newEmail: string): Promise<void> => {
  const currentUser = firebaseAuth.currentUser;
  if (currentUser) {
    await updateEmail(currentUser, newEmail);
  }
};

export const sendEmailVerificationAuth = async (): Promise<void> => {
  const currentUser = await getCurrentUser();
  if (currentUser) {
    await sendEmailVerification(currentUser);
  }
};

export const verifyPassword = async (password: string): Promise<boolean> => {
  try {
    const currentUser = await getCurrentUser();
    if (currentUser) {
      const credential = EmailAuthProvider.credential(
        currentUser.email!,
        password
      );
      await reauthenticateWithCredential(currentUser, credential);
      return true;
    }
    return false;
  } catch (error) {
    if (error instanceof FirebaseError) {
      return error.code === AUTH_ERRORS.MULTI_FACTOR_REQUIRED;
    }
    return false;
  }
};

export interface UserPreference {
  title?: string;
  occupation?: string;
  country?: string;
  city?: string;
  state?: string;
  transcriptionLanguageId?: number;
  visitRetentionPeriodId?: number;
}

export interface PopulatedUserPreference extends UserPreference {
  visitRetentionPeriod?: VisitRetentionPeriod;
  transcriptionLanguage?: TranscriptionLanguage;
}

export interface UserData {
  id: number;
  createDate: string;
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  status: number;
  stripeCustomerId: string;
  planName: PlanName;
  planStatus: PlanStatusActive;
  periodEnd: number;
  periodEndCancel: number;
  style: string;
  format: string;
  recordingUUID: string;
  recordingTime: string;
  retentionPeriod: number;
  deleteReason: string;
  deleteDate: string;
  deleteCompleteDate: string;
  f_uid: string | null;
  userPreference: UserPreference;
}

export const getUser = async ({
  email,
}: Pick<LoginFormValues, "email">): Promise<{
  error: boolean;
  user: UserData | null;
}> => {
  try {
    const response = await axiosClient.post<UserData[]>(
      "/get-user",
      { email },
      {
        sendToken: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "text/plain; charset=utf-8",
        },
      }
    );

    const user = response?.data?.[0] || null;

    if (response.status === 200) {
      if (user) {
        return { error: false, user };
      } else {
        return { error: true, user };
      }
    } else {
      return { error: true, user };
    }
  } catch {
    return { error: true, user: null };
  }
};

export interface AddUserParameters {
  email: string;
  firstname: string;
  lastname: string;
  password: string;
  occupationId?: number;
  turnstileToken?: string;
}

export const addUser = async ({
  email,
  firstname,
  lastname,
  password,
  occupationId,
  turnstileToken,
}: AddUserParameters): Promise<{
  message: string;
  userId: number;
}> => {
  const response = await axiosClient.post<{
    message: string;
    userId: number;
  }>(
    "/v2/add-user",
    {
      email,
      firstname,
      lastname,
      password,
      occupationId,
      "cf-turnstile-response": turnstileToken,
    },
    {
      sendToken: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "text/plain; charset=utf-8",
      },
    }
  );
  return response.data;
};

export const updateUser = async (
  data: UpdateUserAPIRequest
): Promise<UpdateUserAPIResponse> => {
  const response = await axiosClient.post<UpdateUserAPIResponse>(
    "/update-user",
    data,
    {
      sendToken: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "text/plain; charset=utf-8",
      },
    }
  );
  return response.data;
};
